import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Sidebar } from "./Sidebar";
import { HealthcareSidebar } from "./HealthcareSidebar";
import { CallProvider } from "./ActiveCallOverlay";
import { SessionTimeoutWarning } from "./SessionTimeoutWarning";
import WelcomeGate from "../welcome/WelcomeGate";
import ClockInGate from "../timeclock/ClockInGate";
import UnsavedChangesIndicator from "./UnsavedChangesIndicator";
import GlobalQuickActions from "./GlobalQuickActions";
import PendingSyncBadge from "../PendingSyncBadge";
import InstallAppBanner from "../pwa/InstallAppBanner";
import AnnouncementsProvider from "../announcements/AnnouncementsProvider";
import { ContactSlideoverProvider } from "../contacts/ContactDetailSlideover";
import CommsDock from "../comms/CommsDock";
import DeskPhoneCallCompanion from "../comms/DeskPhoneCallCompanion";
import AutoCallNotePrompt from "../comms/AutoCallNotePrompt";
import { toast } from "sonner";
import { authAPI } from "../../services/api";
import { DarkModeProvider } from "../../pages/healthcare/DarkModeContext";
import { isTeamDomain, canAccessComms } from "../../lib/domainMode";
import { cn } from "../../lib/utils";
import { Menu, X } from "lucide-react";

const HEARTBEAT_INTERVAL = 60000; // 60 seconds
const AWAY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

export const MainLayout = () => {
  const { isAuthenticated, loading, user, updateStatus } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const isAutoAwayRef = useRef(false);
  const prevStatusRef = useRef(null);
  const location = useLocation();

  const isHealthcare = location.pathname.startsWith("/healthcare") || location.pathname === "/pcw" || location.pathname.startsWith("/pcw/");
  const isMessages = location.pathname.startsWith("/messages");
  // Only the visit DETAIL screen (with :visitId) is a full-screen
  // tablet flow. The picker at the bare /field-visit path is a
  // regular list view and should keep the normal app chrome.
  const isFieldVisit = /^\/healthcare\/field-visit\/[^/]+/.test(location.pathname);
  // Embedded mode: the /phone workspace loads several legacy pages
  // inside iframes for tab-style parity (Contacts / Text Blast / Fax).
  // When that happens, hide the sidebar + top bar + FABs so the tab
  // body isn't buried inside another app shell (iter 455 bug — the
  // Text Blast tab was showing the Dashboard-with-sidebar because
  // MainLayout wrapped the Broadcasts page with the full nav).
  const isEmbedded = new URLSearchParams(location.search).get("embed") === "1";
  // Pipeline Board / Client Status: as of Feb 19, 2026 the simplified flat
  // list lives inside the normal healthcare chrome (kanban full-viewport
  // mode is no longer needed).
  // The main left rail collapses to icon-only when the user is in healthcare
  // OR team chat — gives the conversation list and message canvas more
  // breathing room.
  const sidebarCollapsed = isHealthcare || isMessages;
  const teamOnly = isTeamDomain();
  const showComms = canAccessComms(user?.role);

  // Track user activity (mouse, keyboard, clicks)
  // Note: status is held in a ref so the activity handler stays stable —
  // no point re-creating the handler every time the user's status updates,
  // that's what caused the heartbeat-restart cascade (ERR_ABORTED storm).
  const userStatusRef = useRef(user?.status);
  useEffect(() => { userStatusRef.current = user?.status; }, [user?.status]);

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    // If user was auto-set to away, restore previous status
    if (isAutoAwayRef.current) {
      isAutoAwayRef.current = false;
      const restore = prevStatusRef.current || "available";
      updateStatus(restore);
    }
  }, [updateStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Activity listeners
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach(e => document.addEventListener(e, handleActivity, { passive: true }));

    // Heartbeat: send every 60s
    const sendHeartbeat = () => {
      authAPI.heartbeat().catch(() => {});
    };
    sendHeartbeat(); // Send immediately on mount
    const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Inactivity check: every 30s, check if idle > 15min → set away
    const inactivityCheck = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle > AWAY_TIMEOUT && !isAutoAwayRef.current) {
        // Read current status from ref so this callback never goes stale.
        prevStatusRef.current = userStatusRef.current !== "away" ? userStatusRef.current : "available";
        isAutoAwayRef.current = true;
        updateStatus("away");
      }
    }, 30000);

    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
      clearInterval(heartbeatInterval);
      clearInterval(inactivityCheck);
    };
    // IMPORTANT: do NOT depend on user?.status — the heartbeat response updates
    // user, which would tear down and rebuild this interval on every heartbeat,
    // firing a second heartbeat immediately and aborting the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, handleActivity, updateStatus]);

  // Domain-based redirects (after all hooks)
  // Block healthcare routes on team-only domain
  if (teamOnly && isHealthcare) {
    // One-time UX toast so the redirect doesn't feel silent. Guarded by a
    // window flag so it doesn't fire twice per navigation (mount + effect).
    try {
      if (!window.__hc_domain_toast_shown) {
        window.__hc_domain_toast_shown = true;
        toast.info("Healthcare tools live on app.avidahealth.org", {
          description: "You've been sent to the Team Avida dashboard. Open the healthcare app in a new tab to reach clinical pages.",
          action: {
            label: "Open app.avidahealth.org",
            onClick: () => window.open(
              `https://app.avidahealth.org${location.pathname}${location.search}`,
              "_blank", "noopener",
            ),
          },
          duration: 8000,
        });
        // Auto-reset after 30s so a later navigation can toast again if
        // the user re-hits a healthcare URL from bookmarks.
        setTimeout(() => { window.__hc_domain_toast_shown = false; }, 30000);
      }
    } catch { /* SSR / private mode noop */ }
    return <Navigate to="/dashboard" replace />;
  }
  // PCWs on app domain: redirect non-healthcare routes to healthcare
  if (!teamOnly && !showComms && !isHealthcare) {
    return <Navigate to="/healthcare" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ── Field (PCW) confinement (Staff/PCW isolation, Phase 1) ──────
  // A caregiver account may only reach the field surface; every office
  // route bounces to the PCW portal. This is the UI half of the realm
  // wall (the backend rejects office API calls from field tokens too).
  if (user?.role === "pcw") {
    const FIELD_ROUTES = [
      "/pcw",
      "/healthcare/pcw-portal",
    ];
    const p = location.pathname;
    const allowed = FIELD_ROUTES.some((r) => p === r || p.startsWith(r + "/"));
    if (!allowed) return <Navigate to="/healthcare/pcw-portal" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SessionTimeoutWarning />
      <WelcomeGate />
      <ClockInGate />
      <CallProvider user={user}>
        <ContactSlideoverProvider>
        {isEmbedded ? (
          // Embedded mode (iter 455): rendered inside an iframe from
          // the /phone workspace. Ship just the outlet — no sidebar,
          // no topbar, no FABs, no announcement rails — so the tab
          // body looks like a native panel instead of a nested app.
          <main className="min-h-screen bg-white" data-testid="main-content">
            <Outlet />
          </main>
        ) : isFieldVisit ? (
          // Full-screen tablet flow — no top bar, no sidebars.
          // FieldVisitFlow ships its own VisitHeader.
          <main className="min-h-screen" data-testid="main-content">
            <Outlet />
          </main>
        ) : (
        <>
        {/* Mobile-only top bar — hamburger + logo. Renders below md
            (≤767 px) so coordinators on phones can actually open the
            sidebar. Hidden on desktop where the rail is permanent. */}
        <header
          className="md:hidden fixed top-0 left-0 right-0 h-12 bg-navy-600 border-b border-navy-500 flex items-center justify-between px-3 z-30 shadow-md"
          data-testid="mobile-topbar"
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(v => !v)}
            className="text-white/90 hover:text-yellow-400 p-1.5 rounded-md hover:bg-white/10 transition-colors"
            data-testid="mobile-hamburger"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <img src="/logos/avida-logo.png" alt="Avida" className="w-7 h-7 rounded-md" />
            <span className="text-sm font-bold text-white">{isHealthcare ? "Avida Health" : isTeamDomain() ? "Team Avida" : "Avida"}</span>
          </div>
          <div className="w-8" />{/* spacer to balance the hamburger */}
        </header>
        <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} collapsed={sidebarCollapsed} />
        {isHealthcare ? (
          <DarkModeProvider>
            <HealthcareSidebar mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} />
            <main
              className={cn(
                "min-h-screen pt-12 lg:pt-0 transition-all duration-300 overflow-x-hidden",
                "lg:ml-[288px]",
              )}
              data-testid="main-content"
            >
              <Outlet />
            </main>
          </DarkModeProvider>
        ) : (
          <main
            className={cn(
              "min-h-screen pt-12 md:pt-0 transition-all duration-300 overflow-x-hidden",
              sidebarCollapsed ? "md:ml-16" : "md:ml-64"
            )}
            data-testid="main-content"
          >
            <div className="p-4 md:p-8">
              <Outlet />
            </div>
          </main>
        )}
        </>
        )}
        {!isEmbedded && !isFieldVisit && !isMessages && showComms && user?.role !== "pcw" && <CommsDock />}
        {!isEmbedded && showComms && user?.role !== "pcw" && <DeskPhoneCallCompanion />}
        {!isEmbedded && showComms && user?.role !== "pcw" && <AutoCallNotePrompt />}
        </ContactSlideoverProvider>
      </CallProvider>

      {/* Global overlays — skipped in embed mode so the parent /phone
          workspace's FABs/announcements aren't rendered twice inside
          the iframe (would stack floating buttons on top of each other
          and blast the same announcement banner twice). */}
      {!isEmbedded && (
        <>
          <UnsavedChangesIndicator />
          <GlobalQuickActions />
          <AnnouncementsProvider />
          <PendingSyncBadge />
          <InstallAppBanner />
        </>
      )}

      {/* NB: Toaster is mounted once at the app root in App.js. A
          second mount here would duplicate every toast in the app —
          see iter490 report for the trail. */}
    </div>
  );
};
